import {useIntl} from 'react-intl';
import {useRouter} from 'next/router';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Heading,
  Box,
  Text,
  Button,
  useToast,
  ModalProps,
} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {CoinsIcon} from '@eksab/assets/icons';

import {useCompetitionJoin} from '../../hooks';
import {CompetitionType} from '../../types';

interface JoinCompetitionModalProps extends Omit<ModalProps, 'children'> {
  fees: number;
  canJoin: boolean;
  competitionType: Competition;
  isCapped: boolean;
  canEarn?: boolean;
}

export const JoinCompetitionModal = ({
  fees,
  canJoin,
  isCapped,
  canEarn = false,
  competitionType,
  ...props
}: JoinCompetitionModalProps) => {
  const {
    query: {slug},
    asPath,
    push,
  } = useRouter();
  const format = useIntl().formatNumber;
  const toast = useToast();

  const t = useTranslate();
  const joinMutation = useCompetitionJoin();

  const onCTA = () => {
    mixpanel?.track('Button Clicked', {button: 'competition.join_new', source: asPath});

    if (!canJoin) return push('/store/coins/packages');

    mixpanel?.track('Join_competition', {
      competitionType: competitionType === 'fantasy' ? CompetitionType.FANTASY : CompetitionType.PREDICTION,
      entryFee: fees,
    });

    joinMutation.mutate(slug as string, {
      onSuccess: () => {
        toast({title: t('competition.join.success'), status: 'success'});
        props.onClose();
      },
      onError: (e) => toast({title: e.response?.data.message ?? t('competition.join.error'), status: 'error'}),
    });
  };

  return (
    <Modal motionPreset="slideInBottom" variant="full" size="sm" {...props}>
      <ModalOverlay />

      <ModalContent px="6" textAlign={['start', 'center']} rounded="xl" shadow="sm" overflow="hidden">
        <ModalHeader display="flex" flexDir="column" gap="3" mt={[7, 20]} px={[0, 2]} py="1">
          <Heading textTransform="capitalize" fontSize={['md', 'xl']} fontWeight="800">
            {t('competition.modal.join.heading')}
          </Heading>

          <Text fontSize={['sm', 'md']} fontWeight="500">
            {!canJoin
              ? t('competition.modal.buy.subheading')
              : t(`competition.modal.join.subheading-${competitionType}`)}
          </Text>
        </ModalHeader>
        <ModalCloseButton top="4" size="lg" />

        <ModalBody
          display="flex"
          flexDir={['row', 'column']}
          alignItems="center"
          gap={[3, 1]}
          mt={[2, 3]}
          mb={[4, 7]}
          px="0"
        >
          <Text textTransform="capitalize" fontSize="sm" fontWeight="800">
            {t('competition.modal.fees')}
          </Text>

          <Box display="flex" gap="3" alignItems="center" fontSize="xl" fontWeight="500">
            <CoinsIcon width="7" height="8" />
            {format(fees)}
          </Box>
        </ModalBody>

        <ModalFooter flexDir={canJoin && !canEarn ? 'column' : 'row'} pt="0" px="0" pb="7" gap="2.5">
          {canEarn ? (
            <>
              <Button fontSize="lg" variant="outline" w="full" tabIndex={1} onClick={onCTA}>
                {t('competition.modal.earn.cta-buy')}
              </Button>

              <Button gap="1.5" w="full" onClick={() => 'TODO: Watch Ad '}>
                <CoinsIcon width="7" height="8" />
                {t('competition.modal.earn.cta-earn')}
              </Button>
            </>
          ) : (
            <Button w="full" tabIndex={1} onClick={onCTA}>
              {t(canJoin ? 'app.actions.join' : 'competition.modal.buy.cta')}
            </Button>
          )}

          {isCapped && canJoin && (
            <Text color="gray.500" fontSize="0.625rem" textAlign="center">
              {t('competition.withdraw.not-withdrawable-brief')}
            </Text>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
